//Built-in libraries

import { TrashIcon } from '@heroicons/react/24/outline';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
//Third party libraries
import * as Yup from 'yup';
import { HookSelect } from '@/components/UI/HookForm/Form/Select';
import { sourcesList } from '../../../../constants';
//Services
import { formatNumber, projectBudgetTotal } from '../../../../utils';
//Custom components
import Button from '../../../UI/Button';
import HookMaskedInput from '../../../UI/HookForm/Form/MaskedInput';
import { CurrencyFormatDisplay } from '../../../UI/NumberFormatDisplay/CurrencyFormatDisplay';

export const projectSourcesValidationSchema = Yup.object({
  sources: Yup.array().of(
    Yup.object({
      name: Yup.string().required('This is required'),
      amount: Yup.number().required('This is required').min(0, 'Amount must be 0 or greater'),
    })
  ),
});

const ProjectSourcesHookFieldArray = () => {
  const { trigger, getValues } = useFormContext();
  const { fields, append, remove } = useFieldArray({ name: 'sources' });
  const projectSourcesFormValues = useWatch({ name: 'sources' });
  const budgetTotal = formatNumber(projectBudgetTotal(projectSourcesFormValues));
  const addNewSource = async () => {
    const projectSourcesValidation = await trigger('sources');
    if (projectSourcesValidation || getValues('sources').length === 0) {
      append({ name: '', amount: 0 });
    }
  };

  return (
    <>
      {fields.length <= 0 && <p className='text-sm text-tertiaryColor-normal'>There are no sources added into the project yet.</p>}
      {fields.map((sources: any, idx) => {
        return (
          <div key={sources.id} className='flex items-center justify-between gap-4 my-4'>
            <div className='grid grid-cols-2 gap-5 w-full'>
              <HookSelect
                customClassName='col-span-1'
                label='Source'
                name={`sources.${idx}.name` as const}
                id='name'
                options={sourcesList.map((d) => ({
                  label: d,
                  value: d,
                }))}
                required={false}
              />
              <HookMaskedInput
                customClassName='col-span-1'
                label='Amount ($)'
                name={`sources.${idx}.amount` as const}
                id='amount'
                maskedComponent='currency'
              />
            </div>
            <div
              className='h-full border p-2 mt-6 rounded-md text-neutralColor-normal bg-gray-50 hover:bg-tertiaryColor-lightest hover:text-tertiaryColor-normal cursor-pointer transition duration-100 ease-in-out'
              onClick={() => remove(idx)}
            >
              <TrashIcon className='h-5 w-6' />
            </div>
          </div>
        );
      })}
      <p className='mt-5 font-bold text-primaryColor-normal'>
        Total: <CurrencyFormatDisplay value={budgetTotal} prefix='$ ' />
      </p>
      <div className='mt-8'>
        <Button label='Add Source' btnStyle='btn-primary' btnSize='btn-sm' type='button' onClick={addNewSource} />
      </div>
    </>
  );
};

export default ProjectSourcesHookFieldArray;
